import * as React from "react";

import { AlertDialog } from "../../components/ui/alert-dialog";
import { Box } from "../../components/ui/box";
import type { SpacingToken } from "../../components/ui/box-classes";
import { Button } from "../../components/ui/button";
import { Icon } from "../../components/ui/icon";
import { Kbd } from "../../components/ui/kbd";
import { Separator } from "../../components/ui/separator";
import {
  Menu,
  MenuItem,
  MenuPopup,
  MenuTrigger,
} from "../../components/ui/menu";
import {
  Popover,
  usePopoverSheet,
  PopoverPopup,
  PopoverTrigger,
} from "../../components/ui/popover";
import { Text } from "../../components/ui/text";
import {
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
} from "../../components/ui/tooltip";
import { useIsMobile } from "../../hooks/use-media-query";
import { cn } from "../../lib/utils";

import type {
  BulkAction,
  BulkSelectionNoun,
  BulkActionConfirm,
  BulkActionDirect,
  BulkActionOptionPicker,
  BulkActionPopover,
  BulkActionRun,
  SelectAllGroup,
} from "./bulk-actions-bar-types";
import { BulkActionsMoreDialog } from "./bulk-actions-dialog";
import {
  STRIP_ACTION_ATTR,
  STRIP_FIXED_ATTR,
  STRIP_MORE_ATTR,
  useStripFold,
} from "./bulk-actions-fold";
import {
  isStripIconOnly,
  selectionCountLabel,
  ActionIcon,
  BAR_TOOLTIP_OFFSET,
  BarButton,
  ConfirmActionPopup,
  executeBulkAction,
  resolveConfirmText,
  resolveSuccessMessage,
  type ActionButtonSharedProps,
  type BarButtonProps,
} from "./bulk-actions-shared";
import type { Omittable } from "../../lib/props";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export type BulkActionsBarProps<TData> = {
  actions: BulkAction<TData>[];
  /** The selected row ids. The bar renders nothing while this is empty. */
  selectedIds: ReadonlySet<string>;
  onClearSelection: () => void;
  /**
   * Row data for every selected id, accumulated by the consumer across page
   * loads so a selection survives page eviction during virtual scroll. It is
   * the ONLY selection source: the consumer fills it in the same render that
   * produces `selectedIds`, from the same rows, so nothing selectable can be
   * selected and missing here.
   */
  selectedRowData: Map<string, TData>;
  /** Groups where "select all" is active (includes unloaded rows). */
  selectAllGroups: SelectAllGroup[];
  /** The count chip's noun ("Project"/"Projects"); omitted = "N selected". */
  selectionNoun?: BulkSelectionNoun | undefined;
};

/**
 * The leaf control for one bar action: icon + label, or — `iconOnly` — the
 * icon alone on the square 32px rung with the label as its accessible name.
 * An icon-only leaf is already wrapped in the `TooltipTrigger`, directly
 * around the Button, so a Menu/Popover trigger that takes this as its
 * `render` keeps the tooltip's hover handlers on the element they belong to.
 * Pair with {@link BarActionTooltip} around the whole action.
 */
function BarActionLeaf<TData>({
  action,
  reason,
  disabled,
  children,
  ...props
}: BarButtonProps & {
  action: BulkAction<TData>;
  /** The selection-level block (`disabledReason`): disables the leaf; the
   *  bubble that explains it is BarActionTooltip's, around the whole action,
   *  so the icon-only leaf does not mount its own trigger here. */
  reason?: string | undefined;
}): React.ReactElement {
  const iconOnly = isStripIconOnly(action);
  const button = (
    <BarButton
      aria-label={iconOnly ? action.label : undefined}
      destructive={action.destructive}
      disabled={disabled || reason != null}
      size={iconOnly ? "icon" : undefined}
      {...props}
    >
      <ActionIcon icon={action.icon} />
      {iconOnly ? null : action.label}
      {children}
    </BarButton>
  );
  return iconOnly && reason == null ? (
    <TooltipTrigger render={button} />
  ) : (
    button
  );
}

/**
 * The tooltip root + bubble for an icon-only action; a pass-through
 * otherwise. With a `reason` the bubble explains the block instead — for
 * every action, from a span around it, since a disabled button swallows the
 * hover the trigger would need.
 */
function BarActionTooltip<TData>({
  action,
  reason,
  children,
}: {
  action: BulkAction<TData>;
  reason?: string | undefined;
  children: React.ReactElement;
}): React.ReactElement {
  const iconOnly = isStripIconOnly(action);
  if (reason != null) {
    return (
      <Tooltip>
        <TooltipTrigger render={<Box as="span" className="inline-flex" />}>
          {children}
        </TooltipTrigger>
        <TooltipPopup sideOffset={BAR_TOOLTIP_OFFSET}>
          {iconOnly ? `${action.label}: ${reason}` : reason}
        </TooltipPopup>
      </Tooltip>
    );
  }
  if (!iconOnly) return children;
  return (
    <Tooltip>
      {children}
      <TooltipPopup sideOffset={BAR_TOOLTIP_OFFSET}>{action.label}</TooltipPopup>
    </Tooltip>
  );
}

// ---------------------------------------------------------------------------
// Popover content frame
// PopoverPopup owns a fixed viewport padding, so the `padding`/`width` props
// are honored by cancelling that padding and re-applying the requested
// token/width on an inner Box.
// ---------------------------------------------------------------------------

function PopoverContentFrame({
  padding,
  width,
  children,
}: Omittable<{
  padding?: SpacingToken;
  width?: number;
  children: React.ReactNode;
}>): React.ReactElement {
  if (padding == null && width == null) return <>{children}</>;
  return (
    <Box
      className={cn(
        padding != null && "-mx-(--viewport-inline-padding) -my-4",
      )}
      padding={padding}
      style={width != null ? { width } : undefined}
    >
      {children}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Per-action button components
// ---------------------------------------------------------------------------

function DirectActionButton<TData>({
  action,
  selectedRows,
  displayCount,
  selectAllGroups,
  loadingActionId,
  onClearSelection,
  setLoadingActionId,
}: ActionButtonSharedProps<TData> & {
  action: BulkActionDirect<TData>;
}): React.ReactElement {
  const isLoading = loadingActionId === action.id;
  const isDisabled = loadingActionId !== null && !isLoading;
  const reason = action.disabledReason?.(selectedRows, selectAllGroups);

  return (
    <BarActionTooltip action={action} reason={reason}>
      <BarActionLeaf
        action={action}
        disabled={isDisabled}
        loading={isLoading}
        reason={reason}
        onClick={() =>
          executeBulkAction({
            actionId: action.id,
            actionFn: () => action.onAction(selectedRows),
            label: action.label,
            // The header and popover actions announce displayCount (the
            // select-all total, including unloaded rows) — the toast must match.
            count: displayCount,
            onClearSelection,
            setLoadingActionId,
            successMessage: resolveSuccessMessage(
              action.successMessage,
              selectedRows,
              displayCount,
            ),
            quiet: action.quiet,
          })
        }
      />
    </BarActionTooltip>
  );
}

function OptionPickerActionButton<TData>({
  action,
  selectedRows,
  displayCount,
  selectAllGroups,
  loadingActionId,
  onClearSelection,
  setLoadingActionId,
}: ActionButtonSharedProps<TData> & {
  action: BulkActionOptionPicker<TData>;
}): React.ReactElement {
  const isLoading = loadingActionId === action.id;
  const isDisabled = loadingActionId !== null && !isLoading;
  const reason = action.disabledReason?.(selectedRows, selectAllGroups);

  return (
    <BarActionTooltip action={action} reason={reason}>
      <Menu>
        <MenuTrigger
          render={
            <BarActionLeaf
              action={action}
              disabled={isDisabled}
              loading={isLoading}
              reason={reason}
            />
          }
        />
        <MenuPopup align="end" side="top">
        {action.options.map((option) => (
          <MenuItem
            key={option.id}
            onClick={() =>
              executeBulkAction({
                actionId: action.id,
                actionFn: () => action.onAction(selectedRows, option.id),
                label: action.label,
                count: displayCount,
                onClearSelection,
                setLoadingActionId,
                successMessage: resolveSuccessMessage(
                  action.successMessage,
                  selectedRows,
                  displayCount,
                  option.id,
                ),
              })
            }
          >
            <ActionIcon color="muted" icon={option.icon} />
            {option.label}
          </MenuItem>
        ))}
        </MenuPopup>
      </Menu>
    </BarActionTooltip>
  );
}

function ConfirmActionButton<TData>({
  action,
  selectedRows,
  displayCount,
  selectAllGroups,
  loadingActionId,
  onClearSelection,
  setLoadingActionId,
}: ActionButtonSharedProps<TData> & {
  action: BulkActionConfirm<TData>;
}): React.ReactElement {
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const isLoading = loadingActionId === action.id;
  const isDisabled = loadingActionId !== null && !isLoading;
  const reason = action.disabledReason?.(selectedRows, selectAllGroups);

  return (
    <AlertDialog onOpenChange={setConfirmOpen} open={confirmOpen}>
      <BarActionTooltip action={action} reason={reason}>
        <BarActionLeaf
          action={action}
          disabled={isDisabled}
          loading={isLoading}
          reason={reason}
          onClick={() => setConfirmOpen(true)}
        />
      </BarActionTooltip>
      <ConfirmActionPopup
        action={action}
        onConfirm={() => {
          setConfirmOpen(false);
          executeBulkAction({
            actionId: action.id,
            actionFn: () => action.onAction(selectedRows),
            label: action.label,
            count: displayCount,
            onClearSelection,
            setLoadingActionId,
            successMessage: resolveSuccessMessage(
              action.successMessage,
              selectedRows,
              displayCount,
            ),
          });
        }}
        selectedRows={selectedRows}
      />
    </AlertDialog>
  );
}

/**
 * Inner content wrapper for popover actions. Base UI exposes no context hook
 * for closing a popover from inside it, so the root is controlled and its
 * open-change handler is passed down as `onOpenChange` for programmatic close
 * on commit.
 */
function PopoverActionContent<TData>({
  action,
  selectedRows,
  displayCount,
  selectAllGroups,
  onClearSelection,
  setLoadingActionId,
  closeActionRef,
  closeMessageRef,
  onOpenChange,
}: {
  action: BulkActionPopover<TData>;
  selectedRows: TData[];
  displayCount: number;
  selectAllGroups: SelectAllGroup[];
  onClearSelection: () => void;
  setLoadingActionId: (id: string | null) => void;
  closeActionRef: React.MutableRefObject<
    (BulkActionRun<TData>) | null
  >;
  closeMessageRef: React.MutableRefObject<React.ReactNode>;
  onOpenChange: (open: boolean) => void;
}): React.ReactElement {
  const commit = React.useCallback(
    (
      actionFn: BulkActionRun<TData>,
      successMessage?: React.ReactNode,
    ) => {
      closeActionRef.current = null;
      closeMessageRef.current = undefined;
      onOpenChange(false);
      executeBulkAction({
        actionId: action.id,
        actionFn: () => actionFn(selectedRows),
        label: action.label,
        count: displayCount,
        onClearSelection,
        setLoadingActionId,
        successMessage,
      });
    },
    [
      action.id,
      action.label,
      selectedRows,
      displayCount,
      onClearSelection,
      setLoadingActionId,
      onOpenChange,
      closeActionRef,
      closeMessageRef,
    ],
  );

  const setCloseAction = React.useCallback(
    (
      actionFn: (BulkActionRun<TData>) | null,
      successMessage?: React.ReactNode,
    ) => {
      closeActionRef.current = actionFn;
      closeMessageRef.current = successMessage;
    },
    [closeActionRef, closeMessageRef],
  );

  return (
    <>{action.content({ selectedRows, selectAllGroups, commit, setCloseAction })}</>
  );
}

function PopoverActionButton<TData>({
  action,
  selectedRows,
  displayCount,
  selectAllGroups,
  loadingActionId,
  onClearSelection,
  setLoadingActionId,
}: ActionButtonSharedProps<TData> & {
  action: BulkActionPopover<TData>;
}): React.ReactElement {
  const isLoading = loadingActionId === action.id;
  const isDisabled = loadingActionId !== null && !isLoading;
  const [open, setOpen] = React.useState(false);
  const closeActionRef = React.useRef<
    (BulkActionRun<TData>) | null
  >(null);
  const closeMessageRef = React.useRef<React.ReactNode>(undefined);

  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      setOpen(next);
      if (!next && closeActionRef.current) {
        const actionFn = closeActionRef.current;
        const successMessage = closeMessageRef.current;
        closeActionRef.current = null;
        closeMessageRef.current = undefined;
        executeBulkAction({
          actionId: action.id,
          actionFn: () => actionFn(selectedRows),
          label: action.label,
          count: displayCount,
          onClearSelection,
          setLoadingActionId,
          successMessage,
        });
      }
    },
    [
      action.id,
      action.label,
      selectedRows,
      displayCount,
      onClearSelection,
      setLoadingActionId,
    ],
  );

  const reason = action.disabledReason?.(selectedRows, selectAllGroups);

  return (
    <BarActionTooltip action={action} reason={reason}>
      <Popover onOpenChange={handleOpenChange} open={open}>
        <PopoverTrigger
          render={
            <BarActionLeaf
              action={action}
              disabled={isDisabled}
              loading={isLoading}
              reason={reason}
            />
          }
        />
        <PopoverPopup
        // Named by the button that opened it, so a screen reader says
        // which action the popover edits.
        aria-label={action.label}
        align={action.contentProps?.align ?? "end"}
        className={action.contentProps?.className}
        side={action.contentProps?.side ?? "top"}
      >
        <PopoverContentFrame
          padding={action.contentProps?.padding}
          width={action.contentProps?.width}
        >
          <PopoverActionContent
            action={action}
            closeActionRef={closeActionRef}
            closeMessageRef={closeMessageRef}
            displayCount={displayCount}
            onClearSelection={onClearSelection}
            onOpenChange={handleOpenChange}
            selectAllGroups={selectAllGroups}
            selectedRows={selectedRows}
            setLoadingActionId={setLoadingActionId}
          />
        </PopoverContentFrame>
        </PopoverPopup>
      </Popover>
    </BarActionTooltip>
  );
}

// ---------------------------------------------------------------------------
// Mobile bulk actions sheet.
// Renders each action's content inline within a single popover — nesting
// overlays per action on mobile renders as a blank screen.
// ---------------------------------------------------------------------------

type MobileSharedProps<TData> = {
  visibleActions: BulkAction<TData>[];
  selectedRows: TData[];
  displayCount: number;
  selectAllGroups: SelectAllGroup[];
  onClearSelection: () => void;
  loadingActionId: string | null;
  setLoadingActionId: (id: string | null) => void;
};

/** The sheet-or-popover body of the phone Actions popup. The action LIST
 *  takes a fixed width in a popover; a detail body sizes itself — an inline
 *  selector carries its own min-width (320px), and pinning the frame narrower
 *  clips it against the popup edge. In the sheet the frame takes the sheet's
 *  width and no frame of its own. */
function MobileBulkActionsBody({
  activeAction,
  children,
}: {
  activeAction: { type: string } | null;
  children: React.ReactNode;
}): React.ReactElement {
  const sheet = usePopoverSheet();
  if (sheet) return <>{children}</>;
  return (
    <PopoverContentFrame
      padding="none"
      width={activeAction && activeAction.type !== "direct" ? undefined : 288}
    >
      {children}
    </PopoverContentFrame>
  );
}

function MobileBulkActionsSheet<TData>({
  visibleActions,
  selectedRows,
  displayCount,
  selectAllGroups,
  onClearSelection,
  loadingActionId,
  setLoadingActionId,
}: MobileSharedProps<TData>): React.ReactElement {
  const [open, setOpen] = React.useState(false);
  const [activeActionId, setActiveActionId] = React.useState<string | null>(
    null,
  );
  const closeActionRef = React.useRef<
    (BulkActionRun<TData>) | null
  >(null);
  const closeMessageRef = React.useRef<React.ReactNode>(undefined);
  const activeAction =
    activeActionId != null
      ? (visibleActions.find((a) => a.id === activeActionId) ?? null)
      : null;

  const runDeferred = React.useCallback(() => {
    const actionFn = closeActionRef.current;
    if (!actionFn || !activeAction) return;
    const message = closeMessageRef.current;
    closeActionRef.current = null;
    closeMessageRef.current = undefined;
    executeBulkAction({
      actionId: activeAction.id,
      actionFn: () => actionFn(selectedRows),
      label: activeAction.label,
      count: displayCount,
      onClearSelection,
      setLoadingActionId,
      successMessage: message,
    });
  }, [
    activeAction,
    selectedRows,
    displayCount,
    onClearSelection,
    setLoadingActionId,
  ]);

  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      setOpen(next);
      if (!next) {
        runDeferred();
        setActiveActionId(null);
      }
    },
    [runDeferred],
  );

  const commit = React.useCallback(
    (
      actionFn: BulkActionRun<TData>,
      successMessage?: React.ReactNode,
    ) => {
      if (!activeAction) return;
      closeActionRef.current = null;
      closeMessageRef.current = undefined;
      setActiveActionId(null);
      setOpen(false);
      executeBulkAction({
        actionId: activeAction.id,
        actionFn: () => actionFn(selectedRows),
        label: activeAction.label,
        count: displayCount,
        onClearSelection,
        setLoadingActionId,
        successMessage,
      });
    },
    [
      activeAction,
      selectedRows,
      displayCount,
      onClearSelection,
      setLoadingActionId,
    ],
  );

  const setCloseAction = React.useCallback(
    (
      actionFn: (BulkActionRun<TData>) | null,
      successMessage?: React.ReactNode,
    ) => {
      closeActionRef.current = actionFn;
      closeMessageRef.current = successMessage;
    },
    [],
  );

  const handleActionClick = React.useCallback(
    (action: BulkAction<TData>) => {
      if (action.type === "direct") {
        setOpen(false);
        executeBulkAction({
          actionId: action.id,
          actionFn: () => action.onAction(selectedRows),
          label: action.label,
          count: displayCount,
          onClearSelection,
          setLoadingActionId,
          successMessage: resolveSuccessMessage(
            action.successMessage,
            selectedRows,
            displayCount,
          ),
          quiet: action.quiet,
        });
        return;
      }
      setActiveActionId(action.id);
    },
    [selectedRows, displayCount, onClearSelection, setLoadingActionId],
  );

  const handleBack = React.useCallback(() => {
    runDeferred();
    setActiveActionId(null);
  }, [runDeferred]);

  return (
    // `responsive`: a bottom sheet on a phone — a detail step's inline
    // selector carries a search that takes the keyboard.
    <Popover onOpenChange={handleOpenChange} open={open} responsive>
      <PopoverTrigger
        render={
          <BarButton
            disabled={loadingActionId !== null}
            loading={loadingActionId !== null}
          />
        }
      >
        Actions
      </PopoverTrigger>
      <PopoverPopup
        align="end"
        // The sheet's caption: the step's name — "Actions", then the action
        // whose detail is open.
        sheetTitle={
          activeAction && activeAction.type !== "direct" ? activeAction.label : "Actions"
        }
      >
        <MobileBulkActionsBody activeAction={activeAction}>
          {activeAction && activeAction.type !== "direct" ? (
            <MobileActionDetail
              action={activeAction}
              commit={commit}
              onBack={handleBack}
              selectAllGroups={selectAllGroups}
              selectedRows={selectedRows}
              setCloseAction={setCloseAction}
            />
          ) : (
            <Box gap="sm" padding="sm">
              {visibleActions.map((action) => {
                const isLoading = loadingActionId === action.id;
                const isDisabled = loadingActionId !== null && !isLoading;
                const reason = action.disabledReason?.(
                  selectedRows,
                  selectAllGroups,
                );
                return (
                  <BarButton
                    justify="start"
                    destructive={action.destructive}
                    disabled={isDisabled || reason != null}
                    fullWidth
                    key={action.id}
                    loading={isLoading}
                    onClick={() => handleActionClick(action)}
                  >
                    <ActionIcon icon={action.icon} />
                    {action.label}
                    {reason != null ? (
                      // The block, trailing: a disabled row on a phone has
                      // no hover to carry a tooltip.
                      <Text className="ms-auto" size="xs" variant="muted">
                        {reason}
                      </Text>
                    ) : null}
                  </BarButton>
                );
              })}
            </Box>
          )}
        </MobileBulkActionsBody>
      </PopoverPopup>
    </Popover>
  );
}

type MobileActionDetailAction<TData> = Exclude<
  BulkAction<TData>,
  BulkActionDirect<TData>
>;

function MobileActionDetail<TData>({
  action,
  selectedRows,
  selectAllGroups,
  commit,
  setCloseAction,
  onBack,
}: Omittable<{
  action: MobileActionDetailAction<TData>;
  selectedRows: TData[];
  selectAllGroups: SelectAllGroup[];
  commit: (
    actionFn: BulkActionRun<TData>,
    successMessage?: React.ReactNode,
  ) => void;
  setCloseAction: (
    actionFn: (BulkActionRun<TData>) | null,
    successMessage?: React.ReactNode,
  ) => void;
  onBack: () => void;
}>): React.ReactElement {
  return (
    <Box>
      <Box alignSelf="start"
        align="center"
        as="button"
        direction="row"
        gap="xs"
        onClick={onBack}
        paddingX="md"
        paddingY="sm"
      >
        <Icon color="muted" name="chevron-left" size="lg" />
        <Text size="sm" variant="muted" weight="normal">
          Back
        </Text>
      </Box>
      {renderMobileActionBody(
        action,
        selectedRows,
        selectAllGroups,
        commit,
        setCloseAction,
        onBack,
      )}
    </Box>
  );
}

function renderMobileActionBody<TData>(
  action: MobileActionDetailAction<TData>,
  selectedRows: TData[],
  selectAllGroups: SelectAllGroup[],
  commit: (
    actionFn: BulkActionRun<TData>,
    successMessage?: React.ReactNode,
  ) => void,
  setCloseAction: (
    actionFn: (BulkActionRun<TData>) | null,
    successMessage?: React.ReactNode,
  ) => void,
  onCancel: () => void,
): React.ReactNode {
  if (action.type === "option-picker") {
    return (
      <Box gap="xs" padding="sm">
        {action.options.map((option) => (
          <Button
            key={option.id}
            onClick={() =>
              commit(
                (rows) => action.onAction(rows, option.id),
                resolveSuccessMessage(
                  action.successMessage,
                  selectedRows,
                  selectedRows.length,
                  option.id,
                ),
              )
            }
            size="sm"
            variant="ghost"
          >
            <ActionIcon color="muted" icon={option.icon} />
            {option.label}
          </Button>
        ))}
      </Box>
    );
  }

  if (action.type === "confirm") {
    const title = resolveConfirmText(action.confirmTitle, selectedRows);
    const description = resolveConfirmText(
      action.confirmDescription,
      selectedRows,
    );
    const count =
      selectAllGroups.length > 0
        ? selectAllGroups.reduce((sum, g) => sum + g.totalCount, 0)
        : selectedRows.length;
    return (
      <Box gap="md" padding="sm">
        <Text weight="semibold">{title}</Text>
        <Text size="sm" variant="muted">
          {description}
        </Text>
        <Box direction="row" gap="sm" justify="end">
          <Button onClick={onCancel} size="sm" variant="ghost">
            Cancel
          </Button>
          <Button
            onClick={() =>
              commit(
                (rows) => action.onAction(rows),
                resolveSuccessMessage(action.successMessage, selectedRows, count),
              )
            }
            size="sm"
            color={action.destructive ? "destructive" : "primary"}
          >
            {action.confirmLabel ?? action.label}
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box padding={action.contentProps?.padding === "none" ? undefined : "sm"}>
      {action.content({ selectedRows, selectAllGroups, commit, setCloseAction })}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Main bar component
// ---------------------------------------------------------------------------

/** The bar's height: 32px controls with an 8px inset above and below. */
const BULK_ACTIONS_BAR_HEIGHT = 48;

/** The bar floats this far above the host's bottom edge. */
const BULK_ACTIONS_BAR_OFFSET = 32;

/**
 * Vertical space the floating bar occupies above the scroll viewport's bottom
 * edge (offset + bar height + breathing room). Hosts that can show the bar
 * append this as PERMANENT in-flow bottom clearance — never gated on
 * selection, which would jolt the scroll height mid-interaction — so the last
 * rows can always scroll out from under it.
 */
export const BULK_ACTIONS_BAR_CLEARANCE = 96;

/**
 * Popups whose own Escape must win over the bar's. Any of these in the DOM —
 * open, or mid-dismiss on the same press — means the key belonged to that
 * layer. Tooltips are labels, not layers, and toasts do not dismiss on Escape.
 */
const OPEN_LAYER_SELECTOR = [
  "alert-dialog",
  "autocomplete",
  "command-dialog",
  "context-menu",
  "dialog",
  "drawer",
  "menu",
  "popover",
  "select",
  "sheet",
]
  .map((slot) => `[data-slot="${slot}-popup"]`)
  .join(", ");

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target.isContentEditable ||
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement
  );
}

/**
 * Escape clears the selection while the bar is up. A convention, not a chord
 * a person has to learn, so it has no row in the shortcuts reference. The key
 * is left alone when a dismissable layer is showing (that layer's Escape
 * closes it — the bar clears on the NEXT press) and when a text field has it
 * (an inline editor cancels on Escape). Listened for in the CAPTURE phase: an
 * open tooltip — the chip's own "Deselect all · Esc" bubble, typically —
 * consumes Escape at the document, and a label must not cost the press it
 * advertises. The layer and field guards above are what keep capture honest.
 */
function useEscapeClearsSelection(onClearSelection: () => void): void {
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      if (isEditableTarget(event.target)) return;
      if (document.querySelector(OPEN_LAYER_SELECTOR)) return;
      event.preventDefault();
      onClearSelection();
    };
    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [onClearSelection]);
}

/** The keycap the deselect chip's tooltip shows for {@link useEscapeClearsSelection}. */
const DESELECT_KEYCAP = "Esc";

export function BulkActionsBar<TData>({
  actions,
  selectedIds,
  onClearSelection,
  selectedRowData,
  selectAllGroups,
  selectionNoun,
}: BulkActionsBarProps<TData>): React.ReactElement | null {
  const isMobile = useIsMobile();
  const [loadingActionId, setLoadingActionId] = React.useState<string | null>(
    null,
  );
  const clusterRef = React.useRef<HTMLDivElement>(null);
  useEscapeClearsSelection(onClearSelection);

  // Derived on every render rather than memoized: `selectedRowData` is a
  // stable ref the consumer mutates in place as pages load, so no dependency
  // list could see it change. The work is O(selected rows), and every child
  // below re-renders with this component anyway.
  const selectedRows = Array.from(selectedIds, (id) => selectedRowData.get(id))
    .filter((r): r is TData => r != null);

  // When select-all is active, display the total count (including unloaded rows)
  const displayCount =
    selectAllGroups.length > 0
      ? selectAllGroups.reduce((sum, g) => sum + g.totalCount, 0)
      : selectedRows.length;

  const visibleActions = actions.filter(
    (a) => !a.hidden?.(selectedRows, selectAllGroups),
  );

  // The strip renders only the non-overflow actions; the mobile sheet and the
  // More dialog list every visible action. The More trigger mounts once any
  // visible action is overflow — declared, or FOLDED: the labeled actions
  // the cluster cannot seat fold into More, last first, so a narrow window
  // never wraps or clips the strip. Icon-only glyphs (Archive beside Delete)
  // never fold: they cost 32px and are the lifecycle verbs the strip is for.
  // More sits at the strip's END past the destructive glyphs, behind a
  // hairline whenever strip actions precede it.
  const stripActions = visibleActions.filter((a) => !a.overflow);
  const hasDeclaredOverflow = visibleActions.length > stripActions.length;
  const labeledStripActions = stripActions.filter(
    (a) => !a.destructive && !isStripIconOnly(a),
  );
  const glyphStripActions = stripActions.filter(
    (a) => !a.destructive && isStripIconOnly(a),
  );
  const destructiveStripActions = stripActions.filter((a) => a.destructive);
  const labeledIds = labeledStripActions.map((a) => a.id);
  const fitCount = useStripFold(clusterRef, labeledIds, {
    reserveMore: !hasDeclaredOverflow,
  });
  const seatedStripActions = labeledStripActions.slice(0, fitCount);
  const hasOverflow =
    hasDeclaredOverflow || seatedStripActions.length < labeledStripActions.length;

  if (
    (selectedRows.length === 0 && selectAllGroups.length === 0) ||
    visibleActions.length === 0
  ) {
    return null;
  }

  const shared: ActionButtonSharedProps<TData> = {
    selectedRows,
    displayCount,
    selectionNoun,
    selectAllGroups,
    loadingActionId,
    onClearSelection,
    setLoadingActionId,
  };

  const renderActionButton = (
    action: BulkAction<TData>,
  ): React.ReactElement => {
    switch (action.type) {
      case "direct":
        return <DirectActionButton action={action} key={action.id} {...shared} />;
      case "option-picker":
        return (
          <OptionPickerActionButton action={action} key={action.id} {...shared} />
        );
      case "confirm":
        return <ConfirmActionButton action={action} key={action.id} {...shared} />;
      case "popover":
        return <PopoverActionButton action={action} key={action.id} {...shared} />;
      default: {
        const _exhaustive: never = action;
        return _exhaustive;
      }
    }
  };

  return (
    <Box position="absolute" zIndex={40}
      // Its own named region: the actions here can share a name with a
      // control of the view under it ("Stage" is also a grouping button).
      role="region"
      aria-label="Bulk actions"
      align="center"
      bottom={BULK_ACTIONS_BAR_OFFSET}
      direction="row"
      // The count chip anchors the start and the actions cluster the end,
      // so the strip reads as a toolbar: what is selected on the left, what
      // can be done to it on the right.
      justify="between"
      gap="lg"
      height={BULK_ACTIONS_BAR_HEIGHT}
      // The strip spans the host with the same inset on both sides as its
      // bottom offset (tighter on mobile), so it frames the view's bottom
      // edge instead of hugging its own content.
      insetX={isMobile ? 16 : BULK_ACTIONS_BAR_OFFSET}
      paddingX="sm"
      rounded="xl"
      // The bar floats over the whole view, so it is its own surface —
      // popover — regardless of what the table underneath it paints.
      surface="popover"
      // A fixed-dark strip in both themes (the tooltip's mechanism: `dark`
      // scopes the strip to the dark token pair, so on a light page it
      // reads as a dark plate). In dark mode the dark popover fill sits one
      // L-point over the page, so the strip lifts toward ink to separate.
      className="dark animate-slide-up-fade border [html.dark_&]:bg-[color-mix(in_oklab,var(--popover)_90%,var(--foreground))]"
    >
      {/* Count chip doubles as the deselect-all control: a labelled, outlined
          chip with a trailing × reads as "removable", so clearing the whole
          selection is one obvious click rather than a bare icon that scans as
          "close". The strip's hairline token barely separates from its lifted
          fill, so the chip edge is ink at low alpha — the hairline the tooltip
          gives its kbd chips on the same inverted surface. */}
      <Tooltip>
        <TooltipTrigger
          render={
            <Button
              aria-label="Deselect all"
              // Full ink: the count is the strip's subject, not one of its
              // quiet actions, so it reads a rung above them.
              className="border-foreground/20 text-foreground"
              onClick={onClearSelection}
              shape="square"
              variant="outline"
            />
          }
        >
          {selectionCountLabel(displayCount, selectionNoun)}
          <Icon name="x" size="sm" />
        </TooltipTrigger>
        <TooltipPopup sideOffset={BAR_TOOLTIP_OFFSET}>
          <Box align="center" direction="row" gap="xs">
            <Text size="xs">Deselect all</Text>
            <Kbd>{DESELECT_KEYCAP}</Kbd>
          </Box>
        </TooltipPopup>
      </Tooltip>

      {isMobile ? (
        <MobileBulkActionsSheet
          displayCount={displayCount}
          loadingActionId={loadingActionId}
          onClearSelection={onClearSelection}
          selectAllGroups={selectAllGroups}
          selectedRows={selectedRows}
          setLoadingActionId={setLoadingActionId}
          visibleActions={visibleActions}
        />
      ) : (
        // Parent-driven width (`flex: 1 1 0`, min-width 0): the cluster is
        // exactly the room left of the count chip whatever it seats, which
        // is what lets the fold measure it without feeding back into it.
        <Box
          align="center"
          direction="row"
          fill="flex"
          gap="xs"
          justify="end"
          ref={clusterRef}
        >
          {seatedStripActions.map((action) => (
            <Box
              as="span"
              display="inline-flex"
              key={action.id}
              shrink={false}
              {...{ [STRIP_ACTION_ATTR]: action.id }}
            >
              {renderActionButton(action)}
            </Box>
          ))}
          {[...glyphStripActions, ...destructiveStripActions].map((action) => (
            <Box
              as="span"
              display="inline-flex"
              key={action.id}
              shrink={false}
              {...{ [STRIP_FIXED_ATTR]: "" }}
            >
              {renderActionButton(action)}
            </Box>
          ))}
          {hasOverflow ? (
            <Box
              align="center"
              alignSelf="stretch"
              as="span"
              display="inline-flex"
              shrink={false}
              {...{ [hasDeclaredOverflow ? STRIP_FIXED_ATTR : STRIP_MORE_ATTR]: "" }}
            >
              {stripActions.length > 0 ? (
                // The strip's hairline token barely reads on its lifted
                // fill, so the rule is ink at low alpha — the count chip's
                // own edge.
                <Box alignSelf="stretch" direction="row" marginX="xs" marginY="sm" shrink={false}>
                  <Separator className="bg-foreground/20" orientation="vertical" />
                </Box>
              ) : null}
              <BulkActionsMoreDialog actions={visibleActions} {...shared} />
            </Box>
          ) : null}
        </Box>
      )}
    </Box>
  );
}
